import type { ControlValue } from "../../state/controlStore";
import type { ParameterPath } from "../../state/parameterRoutes";
import type { EngineSnapshot, UserControls } from "../../state/types";
import { Slider } from "../ui/Slider";

export interface GenreBlendProps {
  controls: UserControls;
  snapshot: EngineSnapshot;
  setControl: (path: ParameterPath, value: ControlValue) => void;
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * Three independent sliders, normalised internally.
 *
 * They are not a crossfade between three finished styles: the normalised values
 * become weights on the generation rules, so any mix produces one coherent set
 * of rules rather than a blend of three grooves. The normalised readout is
 * shown here because the raw slider numbers alone do not tell you what the
 * engine actually received.
 */
export function GenreBlend({ controls, snapshot, setControl }: GenreBlendProps) {
  const g = snapshot.diagnostics.genreNormalized;
  const total = controls.genre.ambient + controls.genre.techno + controls.genre.jazz;

  return (
    <section className="panel panel--static" aria-labelledby="genre-title">
      <div className="panel__header">
        <h2 id="genre-title" className="transport__title">
          Genre Blend
        </h2>
        <span className="panel__badge">
          A {percent(g.ambient)} / T {percent(g.techno)} / J {percent(g.jazz)}
        </span>
      </div>

      <div className="panel__body panel__body--open">
        <Slider
          label="Ambient"
          value={controls.genre.ambient}
          min={0}
          max={100}
          step={1}
          displayValue={`${controls.genre.ambient} → ${percent(g.ambient)}`}
          onChange={(value) => {
            setControl("genre.ambient", value);
          }}
        />
        <Slider
          label="Techno"
          value={controls.genre.techno}
          min={0}
          max={100}
          step={1}
          displayValue={`${controls.genre.techno} → ${percent(g.techno)}`}
          onChange={(value) => {
            setControl("genre.techno", value);
          }}
        />
        <Slider
          label="Jazz"
          value={controls.genre.jazz}
          min={0}
          max={100}
          step={1}
          displayValue={`${controls.genre.jazz} → ${percent(g.jazz)}`}
          hint="3値は内部で合計1.0へ正規化されます。変更は数小節かけて生成規則へ反映されます。"
          onChange={(value) => {
            setControl("genre.jazz", value);
          }}
        />

        {total === 0 && (
          <p className="transport__notice transport__notice--info" role="status">
            3値とも0のため、内部では 1/3 ずつとして扱っています。
          </p>
        )}
      </div>
    </section>
  );
}
